<template>
  <div class="p-2 sm:p-3 text-lexend min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950">

    <!-- ═══════════════════════════════════════════ -->
    <!-- HEADER STICKY -->
    <!-- ═══════════════════════════════════════════ -->
    <div class="sticky top-0 z-30 -mx-2 sm:-mx-3 px-2 sm:px-3 py-2 mb-3 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50">
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 min-w-0">
          <div class="relative flex-shrink-0">
            <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Icon icon="mdi:clipboard-check" class="text-white text-lg" />
            </div>
            <span v-if="nuevasIds.size > 0" class="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-[9px] font-black rounded-full flex items-center justify-center animate-pulse shadow-lg">
              {{ nuevasIds.size }}
            </span>
          </div>
          <div class="min-w-0">
            <h1 class="text-sm font-black text-gray-900 dark:text-white leading-tight">Asignaciones</h1>
            <p class="text-[10px] font-bold text-gray-500 dark:text-gray-400">
              {{ pendientes.length }} pendiente{{ pendientes.length === 1 ? '' : 's' }}
              <span v-if="ultimaActualizacion" class="text-gray-400"> · act. {{ tiempoDesdeActualizacion }}</span>
            </p>
          </div>
        </div>

        <div class="flex items-center gap-1.5 flex-shrink-0">
          <!-- Botón sonido -->
          <button @click="sonidoActivo = !sonidoActivo"
            class="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center active:scale-95 transition">
            <Icon :icon="sonidoActivo ? 'mdi:volume-high' : 'mdi:volume-off'"
              :class="sonidoActivo ? 'text-emerald-600' : 'text-gray-400'" class="text-sm" />
          </button>

          <!-- Refresh -->
          <button @click="load" :disabled="loading"
            class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center active:scale-95 transition shadow-md shadow-purple-500/30 disabled:opacity-50">
            <Icon icon="mdi:refresh" :class="{ 'animate-spin': loading }" class="text-white text-sm" />
          </button>
        </div>
      </div>

      <!-- Barra de progreso del día -->
      <div v-if="asignaciones.length > 0" class="mt-2 flex items-center gap-2">
        <div class="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div class="h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full transition-all duration-500"
            :style="{ width: pctEntregados + '%' }"></div>
        </div>
        <span class="text-[9px] font-black text-emerald-600 dark:text-emerald-400">
          {{ entregados }}/{{ asignaciones.length }}
        </span>
      </div>

      <!-- ⭐ PESTAÑAS -->
      <div class="mt-2 grid grid-cols-2 gap-1.5 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
        <button
          @click="pestanaActiva = 'pendientes'"
          :class="[
            'flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-black transition-all active:scale-95',
            pestanaActiva === 'pendientes'
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
              : 'text-gray-500 dark:text-gray-400'
          ]"
        >
          <Icon icon="mdi:package-variant-closed" class="text-sm" />
          Pendientes
          <span :class="[
            'px-1.5 py-0.5 rounded-full text-[9px] font-black',
            pestanaActiva === 'pendientes'
              ? 'bg-white/20 text-white'
              : 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300'
          ]">
            {{ pendientes.length }}
          </span>
        </button>

        <button
          @click="pestanaActiva = 'entregados'"
          :class="[
            'flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-black transition-all active:scale-95',
            pestanaActiva === 'entregados'
              ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-md'
              : 'text-gray-500 dark:text-gray-400'
          ]"
        >
          <Icon icon="mdi:check-circle" class="text-sm" />
          Entregados
          <span :class="[
            'px-1.5 py-0.5 rounded-full text-[9px] font-black',
            pestanaActiva === 'entregados'
              ? 'bg-white/20 text-white'
              : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300'
          ]">
            {{ entregadosTotal.length }}
          </span>
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════ -->
    <!-- LOADING -->
    <!-- ═══════════════════════════════════════════ -->
    <div v-if="loading && asignaciones.length === 0" class="flex flex-col items-center justify-center py-16 gap-3">
      <div class="relative">
        <div class="w-16 h-16 rounded-full border-4 border-purple-100 dark:border-purple-900/30"></div>
        <div class="w-16 h-16 rounded-full border-4 border-transparent border-t-purple-600 animate-spin absolute top-0 left-0"></div>
      </div>
      <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Cargando...</p>
    </div>

    <!-- ═══════════════════════════════════════════ -->
    <!-- EMPTY GENERAL -->
    <!-- ═══════════════════════════════════════════ -->
    <div v-else-if="asignaciones.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-10 text-center">
      <div class="w-20 h-20 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
        <Icon icon="mdi:clipboard-text-off" class="text-3xl text-gray-300 dark:text-gray-600" />
      </div>
      <p class="text-xs font-black text-gray-500 dark:text-gray-400">Sin asignaciones por ahora</p>
      <p class="text-[10px] text-gray-400 mt-1">Te avisaremos cuando llegue un pedido</p>
    </div>

    <!-- ═══════════════════════════════════════════ -->
    <!-- CONTENIDO DE LA PESTAÑA -->
    <!-- ═══════════════════════════════════════════ -->
    <div v-else>

      <!-- SELECTOR ITEMS POR PÁGINA -->
      <div class="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div class="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
          <span class="text-[9px] font-black text-gray-400 uppercase tracking-wider px-1">Ver:</span>
          <button
            v-for="n in [5, 10, 20, 50]"
            :key="n"
            @click="itemsPorPagina = n; paginaActual = 1"
            :class="[
              'px-2 py-1 text-[10px] font-black rounded-md transition-all active:scale-95',
              itemsPorPagina === n
                ? 'bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            ]"
          >
            {{ n }}
          </button>
        </div>
        <span class="text-[10px] font-bold text-gray-400">
          Pág. {{ paginaActual }}/{{ totalPaginas }}
        </span>
      </div>

      <!-- EMPTY PESTAÑA -->
      <div v-if="asignacionesPaginadas.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-10 text-center">
        <div class="w-20 h-20 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
          <Icon :icon="pestanaActiva === 'pendientes' ? 'mdi:party-popper' : 'mdi:clipboard-text-off'"
            class="text-3xl text-gray-300 dark:text-gray-600" />
        </div>
        <p class="text-xs font-black text-gray-500 dark:text-gray-400">
          {{ pestanaActiva === 'pendientes'
            ? '¡Todo al día! No hay pendientes'
            : 'Aún no has entregado nada' }}
        </p>
        <p class="text-[10px] text-gray-400 mt-1">
          {{ pestanaActiva === 'pendientes'
            ? 'Te avisaremos cuando llegue un nuevo pedido'
            : 'Cuando entregues aparecerán aquí' }}
        </p>
      </div>

      <!-- LISTA -->
      <TransitionGroup
        v-else
        name="card-list"
        tag="div"
        class="space-y-2.5"
      >
        <div
          v-for="a in asignacionesPaginadas"
          :key="a.idAsignacion"
          :class="[
            'bg-white dark:bg-gray-800 rounded-2xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md active:scale-[0.99]',
            nuevasIds.has(a.idAsignacion)
              ? 'border-amber-400 dark:border-amber-500 ring-2 ring-amber-300/50 dark:ring-amber-500/30'
              : 'border-gray-100 dark:border-gray-700',
            esEntregado(a) ? 'opacity-90' : ''
          ]"
        >
          <!-- ⭐ BARRA DE ESTADO LATERAL -->
          <div class="flex">
            <div :class="['w-1 flex-shrink-0', barraEstado(a.estado)]"></div>

            <div class="flex-1 min-w-0">

              <!-- FILA 1: N° Pedido + Estado + Pago -->
              <div :class="[
                'flex items-center justify-between gap-1 px-3 py-2',
                esEntregado(a)
                  ? 'bg-gradient-to-r from-emerald-600 to-green-600'
                  : 'bg-gradient-to-r from-purple-600 to-indigo-600'
              ]">
                <div class="flex items-center gap-1.5 min-w-0">
                  <Icon :icon="esEntregado(a) ? 'mdi:check-circle' : 'mdi:package-variant-closed'"
                    class="text-white text-xs flex-shrink-0" />
                  <span class="font-black text-white text-xs truncate">#{{ a.numeroPedido }}</span>
                </div>
                <div class="flex items-center gap-1 flex-shrink-0">
                  <span :class="['px-1.5 py-0.5 text-[8px] font-black uppercase rounded whitespace-nowrap', getMetodoPagoColor(a.metodoPagoCliente)]">
                    {{ labelMetodoPago(a.metodoPagoCliente) }}
                  </span>
                  <span :class="['px-1.5 py-0.5 text-[8px] font-black uppercase rounded whitespace-nowrap', getEstadoPagoColor(a.estadoPago)]">
                    {{ labelEstadoPago(a) }}
                  </span>
                </div>
              </div>

              <!-- FILA 2: Cliente -->
              <div class="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                <div class="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Icon icon="mdi:account" class="text-white text-xs" />
                </div>
                <span class="text-xs font-bold text-gray-900 dark:text-white truncate flex-1">
                  {{ a.nombreCliente }} {{ a.apellidoCliente }}
                </span>
                <a v-if="a.telefonoCliente && !esEntregado(a)" :href="`tel:${a.telefonoCliente}`"
                  class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 active:scale-95 transition shadow-sm">
                  <Icon icon="mdi:phone" class="text-sm" />
                </a>
                <a v-if="a.telefonoCliente && !esEntregado(a)"
                  :href="getWhatsappLink(a.telefonoCliente, a.numeroPedido, a.nombreCliente)"
                  target="_blank" rel="noopener"
                  class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 active:scale-95 transition shadow-sm">
                  <Icon icon="mdi:whatsapp" class="text-sm" />
                </a>
              </div>

              <!-- FILA 3: Dirección -->
              <div class="flex items-start gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                <Icon icon="mdi:map-marker" class="text-amber-500 text-sm flex-shrink-0 mt-0.5" />
                <div class="min-w-0 flex-1">
                  <p class="text-[11px] font-bold text-gray-800 dark:text-gray-200 leading-tight break-words">
                    {{ a.direccionEntrega || 'Sin dirección' }}
                  </p>
                  <p v-if="a.puntoReferencia" class="text-[9px] text-gray-500 dark:text-gray-400 leading-tight break-words mt-0.5">
                    <Icon icon="mdi:information-outline" class="text-[10px] inline mr-0.5" />
                    {{ a.puntoReferencia }}
                  </p>
                </div>
              </div>

              <!-- FILA 4: Info mini con chips -->
              <div class="flex items-center gap-1.5 px-3 py-2 text-[9px] flex-wrap">
                <span class="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-md font-black">
                  <Icon icon="mdi:cash" class="text-[10px]" />
                  ${{ formatearMoneda(a.comisionUsd) }}
                </span>
                <span v-if="a.nombreZona" class="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md font-black uppercase text-[8px]">
                  <Icon icon="mdi:map-marker-radius" class="text-[10px]" />
                  {{ a.nombreZona }}
                </span>
                <span v-if="a.distanciaKm" class="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md font-black">
                  <Icon icon="mdi:road-variant" class="text-[10px]" />
                  {{ formatearNumero(a.distanciaKm) }} km
                </span>
                <span class="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md font-bold ml-auto">
                  <Icon icon="mdi:clock-outline" class="text-[10px]" />
                  {{ formatFechaCorta(a.fechaAsignacion) }}
                </span>
              </div>

              <!-- FILA 5: BOTONES DE ACCIÓN -->
              <div class="flex gap-1.5 px-3 py-2.5 bg-gray-50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 flex-wrap">

                <!-- Mapa (solo pendientes) -->
                <template v-if="!esEntregado(a)">
                  <a v-if="a.urlMapaEntrega" :href="a.urlMapaEntrega" target="_blank" rel="noopener"
                    class="h-8 px-3 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg flex items-center justify-center gap-1 text-[10px] font-black uppercase flex-shrink-0 active:scale-95 transition shadow-sm">
                    <Icon icon="mdi:google-maps" class="text-sm" />
                    Mapa
                  </a>
                  <a v-else-if="a.latitudEntrega && a.longitudEntrega"
                    :href="`https://www.google.com/maps?q=${a.latitudEntrega},${a.longitudEntrega}`"
                    target="_blank" rel="noopener"
                    class="h-8 px-3 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg flex items-center justify-center gap-1 text-[10px] font-black uppercase flex-shrink-0 active:scale-95 transition shadow-sm">
                    <Icon icon="mdi:google-maps" class="text-sm" />
                    Mapa
                  </a>
                </template>

                <!-- Aceptar / Rechazar -->
                <template v-if="a.estado === 'asignado'">
                  <button @click="aceptar(a)" :disabled="saving"
                    class="h-8 flex-1 min-w-[100px] bg-gradient-to-br from-emerald-500 to-green-600 text-white rounded-lg flex items-center justify-center gap-1.5 text-[10px] font-black uppercase disabled:opacity-50 active:scale-95 transition shadow-sm">
                    <Icon icon="mdi:check" class="text-sm" />
                    Aceptar
                  </button>
                  <button @click="abrirModalRechazar(a)" :disabled="saving"
                    class="h-8 w-8 bg-gradient-to-br from-rose-500 to-red-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 active:scale-95 transition shadow-sm">
                    <Icon icon="mdi:close" class="text-sm" />
                  </button>
                </template>

                <!-- Ir -->
                <button v-if="a.estado === 'aceptado'" @click="iniciarRuta(a)" :disabled="saving"
                  class="h-8 flex-1 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-lg flex items-center justify-center gap-1.5 text-[10px] font-black uppercase disabled:opacity-50 active:scale-95 transition shadow-sm">
                  <Icon icon="mdi:moped" class="text-sm" />
                  Ir al cliente
                </button>

                <!-- Llegué -->
                <button v-if="a.estado === 'en_camino'" @click="marcarLlegada(a)" :disabled="saving"
                  class="h-8 flex-1 bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-lg flex items-center justify-center gap-1.5 text-[10px] font-black uppercase disabled:opacity-50 active:scale-95 transition shadow-sm animate-pulse">
                  <Icon icon="mdi:map-marker-check" class="text-sm" />
                  Llegué
                </button>

                <!-- Entregar -->
                <button v-if="a.estado === 'en_proceso'" @click="abrirModalCompletar(a)" :disabled="saving"
                  class="h-8 flex-1 bg-gradient-to-br from-emerald-500 to-green-600 text-white rounded-lg flex items-center justify-center gap-1.5 text-[10px] font-black uppercase disabled:opacity-50 active:scale-95 transition shadow-md shadow-emerald-500/30">
                  <Icon icon="mdi:check-all" class="text-sm" />
                  Entregar
                </button>

                <!-- Estados finales -->
                <div v-if="a.estado === 'entregado'"
                  class="h-8 flex-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded-lg flex items-center justify-center gap-1 text-[10px] font-black uppercase">
                  <Icon icon="mdi:check-circle" class="text-sm" />
                  Entregado
                </div>
                <div v-if="a.estado === 'rechazado'"
                  class="h-8 flex-1 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 rounded-lg flex items-center justify-center gap-1 text-[10px] font-black uppercase">
                  <Icon icon="mdi:close-circle" class="text-sm" />
                  Rechazado
                </div>
              </div>

            </div>
          </div>
        </div>
      </TransitionGroup>

      <!-- PAGINACIÓN -->
      <div v-if="totalPaginas > 1" class="flex items-center justify-center gap-1.5 pt-4 flex-wrap">
        <button @click="paginaActual = 1" :disabled="paginaActual === 1"
          class="h-9 w-9 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[11px] font-black disabled:opacity-40 flex items-center justify-center active:scale-95 transition">
          <Icon icon="mdi:chevron-double-left" class="text-sm" />
        </button>
        <button @click="paginaActual = Math.max(1, paginaActual - 1)" :disabled="paginaActual === 1"
          class="h-9 px-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[11px] font-black disabled:opacity-40 flex items-center gap-1 active:scale-95 transition">
          <Icon icon="mdi:chevron-left" class="text-sm" />
          <span class="hidden sm:inline">Ant</span>
        </button>

        <span class="px-3 py-1.5 bg-gradient-to-br from-purple-600 to-indigo-600 text-white text-[11px] font-black rounded-xl shadow-sm">
          {{ paginaActual }}
        </span>
        <span class="text-[11px] font-black text-gray-400">/ {{ totalPaginas }}</span>

        <button @click="paginaActual = Math.min(totalPaginas, paginaActual + 1)" :disabled="paginaActual === totalPaginas"
          class="h-9 px-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[11px] font-black disabled:opacity-40 flex items-center gap-1 active:scale-95 transition">
          <span class="hidden sm:inline">Sig</span>
          <Icon icon="mdi:chevron-right" class="text-sm" />
        </button>
        <button @click="paginaActual = totalPaginas" :disabled="paginaActual === totalPaginas"
          class="h-9 w-9 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[11px] font-black disabled:opacity-40 flex items-center justify-center active:scale-95 transition">
          <Icon icon="mdi:chevron-double-right" class="text-sm" />
        </button>
      </div>

      <div v-if="totalPaginas > 1" class="text-center text-[10px] text-gray-400 py-2 font-bold">
        Mostrando {{ (paginaActual - 1) * itemsPorPagina + 1 }}–{{ Math.min(paginaActual * itemsPorPagina, listaFiltrada.length) }} de {{ listaFiltrada.length }}
      </div>
    </div>

    <!-- ═══════════════════════════════════════════ -->
    <!-- MODAL RECHAZAR -->
    <!-- ═══════════════════════════════════════════ -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showRechazarModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999]" @click.self="showRechazarModal = false">
          <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-xs overflow-hidden">
            <div class="bg-gradient-to-r from-rose-600 to-red-600 px-4 py-3 flex items-center gap-2">
              <div class="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Icon icon="mdi:close-circle" class="text-white text-sm" />
              </div>
              <h3 class="text-xs font-black text-white flex-1">Rechazar #{{ asignacionARechazar?.numeroPedido }}</h3>
              <button @click="showRechazarModal = false" class="w-7 h-7 rounded-lg bg-white/10 text-white flex items-center justify-center">
                <Icon icon="mdi:close" class="text-xs" />
              </button>
            </div>
            <div class="p-4">
              <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Motivo del rechazo <span class="text-rose-500">*</span>
              </label>
              <textarea
                v-model="motivoRechazo"
                rows="3"
                maxlength="200"
                placeholder="Ej: Cliente no está, dirección incorrecta..."
                class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white outline-none resize-none focus:border-rose-500"
              ></textarea>
            </div>
            <div class="px-4 pb-4 flex gap-2">
              <button @click="showRechazarModal = false"
                class="flex-1 py-2.5 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-[11px] uppercase active:scale-95 transition">
                Cancelar
              </button>
              <button @click="ejecutarRechazo" :disabled="saving || !motivoRechazo.trim()"
                class="flex-1 py-2.5 bg-gradient-to-r from-rose-600 to-red-600 text-white rounded-xl font-black text-[11px] uppercase disabled:opacity-50 active:scale-95 transition shadow-md shadow-rose-500/30">
                Rechazar
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ═══════════════════════════════════════════ -->
    <!-- MODAL COMPLETAR ENTREGA -->
    <!-- ═══════════════════════════════════════════ -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showCompletarModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999]" @click.self="cerrarModalCompletar">
          <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm overflow-hidden animate-zoomIn">

            <!-- Header -->
            <div class="bg-gradient-to-r from-emerald-600 to-green-600 px-4 py-3 flex items-center gap-2">
              <div class="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon icon="mdi:lock-check" class="text-white text-base" />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="text-xs font-black text-white uppercase tracking-wider">Confirmar Entrega</h3>
                <p class="text-[10px] text-emerald-100 font-bold truncate">
                  #{{ asignacionACompletar?.numeroPedido }} · {{ asignacionACompletar?.nombreCliente }} {{ asignacionACompletar?.apellidoCliente }}
                </p>
              </div>
              <button @click="cerrarModalCompletar" class="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center flex-shrink-0">
                <Icon icon="mdi:close" class="text-xs" />
              </button>
            </div>

            <!-- Body -->
            <div class="p-4 space-y-3 max-h-[70vh] overflow-y-auto">

              <!-- Info -->
              <div class="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-xl p-3">
                <div class="flex items-start gap-2">
                  <Icon icon="mdi:information-outline" class="text-amber-600 text-sm flex-shrink-0 mt-0.5" />
                  <div>
                    <p class="text-[10px] font-black text-amber-800 dark:text-amber-300 uppercase tracking-wider mb-1">
                      Pide el código al cliente
                    </p>
                    <p class="text-[10px] text-amber-700 dark:text-amber-400 leading-relaxed">
                      El cliente debe darte un código de 6 dígitos para confirmar que recibió su gas.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Input código -->
              <div>
                <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Código de Entrega <span class="text-rose-500">*</span>
                </label>
                <input
                  ref="inputCodigo"
                  v-model="codigoEntrega"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  placeholder="000000"
                  @input="formatearCodigoInput"
                  @paste="handlePaste"
                  @keyup.enter="ejecutarCompletar"
                  class="w-full px-4 py-3 text-center text-2xl font-black tracking-[0.5em] bg-gray-50 dark:bg-gray-900/50 border-2 rounded-xl outline-none transition-all font-mono"
                  :class="[
                    errorCodigo
                      ? 'border-rose-500 dark:border-rose-500 text-rose-600 dark:text-rose-400 animate-shake'
                      : codigoEntrega.length === 6
                        ? 'border-emerald-500 dark:border-emerald-500 text-emerald-600 dark:text-emerald-400'
                        : 'border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:border-emerald-500'
                  ]"
                />
                <p v-if="errorCodigo" class="text-[10px] font-black text-rose-600 dark:text-rose-400 mt-1.5 text-center">
                  <Icon icon="mdi:alert-circle" class="text-xs inline mr-1" />
                  {{ errorCodigo }}
                </p>
                <p v-else class="text-[9px] text-gray-400 mt-1.5 text-center font-bold">
                  <Icon icon="mdi:keyboard" class="text-xs inline mr-1" />
                  6 dígitos · puedes pegar o escribir
                </p>
              </div>

              <!-- Observaciones -->
              <div>
                <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Observaciones <span class="text-gray-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  v-model="observacionesEntrega"
                  rows="2"
                  maxlength="200"
                  placeholder="Ej: Cliente recibió conforme..."
                  class="w-full px-3 py-2 text-xs bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 rounded-lg outline-none resize-none focus:border-emerald-500 dark:text-white"
                ></textarea>
              </div>

              <!-- Propina -->
              <div>
                <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Propina en Bs. <span class="text-gray-400 font-normal">(opcional)</span>
                </label>
                <input
                  v-model.number="propinaBs"
                  type="number"
                  min="0"
                  step="10"
                  placeholder="0.00"
                  class="w-full px-3 py-2 text-sm font-black bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:border-emerald-500 dark:text-white"
                />
              </div>
            </div>

            <!-- Footer -->
            <div class="px-4 pb-4 flex gap-2">
              <button @click="cerrarModalCompletar" :disabled="saving"
                class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-[11px] uppercase disabled:opacity-50 active:scale-95 transition">
                Cancelar
              </button>
              <button @click="ejecutarCompletar" :disabled="saving || codigoEntrega.length !== 6"
                class="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-black text-[11px] uppercase disabled:opacity-50 flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-500/30 active:scale-95 transition">
                <Icon :icon="saving ? 'mdi:loading' : 'mdi:check-all'" :class="{ 'animate-spin': saving }" class="text-sm" />
                {{ saving ? 'Enviando...' : 'Confirmar' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { nextTick } from 'vue';
import {
  obtenerMisAsignaciones,
  aceptarAsignacion,
  rechazarAsignacion,
  iniciarRutaAsignacion,
  marcarLlegadaAsignacion,
  completarEntregaAsignacion,
  actualizarDisponibilidadDelivery
} from '@/api/gas.js';

export default {
  name: 'AsignacionesList',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      asignaciones: [],
      loading: false,
      saving: false,

      // ⭐ Pestaña activa
      pestanaActiva: 'pendientes', // 'pendientes' | 'entregados'

      showRechazarModal: false,
      asignacionARechazar: null,
      motivoRechazo: '',

      showCompletarModal: false,
      asignacionACompletar: null,
      codigoEntrega: '',
      observacionesEntrega: '',
      propinaBs: null,
      errorCodigo: '',

      nuevasIds: new Set(),
      ultimaActualizacion: null,
      pollingInterval: null,
      tickInterval: null,
      sonidoActivo: true,

      paginaActual: 1,
      itemsPorPagina: 10,
    };
  },
  computed: {
    // ⭐ Pedidos PENDIENTES (por hacer)
    pendientes() {
      return this.asignaciones.filter(a =>
        ['asignado', 'aceptado', 'en_camino', 'en_proceso'].includes(a.estado)
      );
    },

    // ⭐ Pedidos ENTREGADOS / RECHAZADOS (historial)
    entregadosTotal() {
      return this.asignaciones.filter(a =>
        ['entregado', 'rechazado'].includes(a.estado)
      );
    },

    // ⭐ Lista según pestaña activa
    listaFiltrada() {
      return this.pestanaActiva === 'pendientes'
        ? this.pendientes
        : this.entregadosTotal;
    },

    asignacionesPaginadas() {
      const inicio = (this.paginaActual - 1) * this.itemsPorPagina;
      return this.listaFiltrada.slice(inicio, inicio + this.itemsPorPagina);
    },

    totalPaginas() {
      return Math.max(1, Math.ceil(this.listaFiltrada.length / this.itemsPorPagina));
    },

    entregados() {
      return this.asignaciones.filter(a => a.estado === 'entregado').length;
    },

    pctEntregados() {
      if (this.asignaciones.length === 0) return 0;
      return Math.round((this.entregados / this.asignaciones.length) * 100);
    },

    tiempoDesdeActualizacion() {
      if (!this.ultimaActualizacion) return '';
      const seg = Math.floor((Date.now() - this.ultimaActualizacion) / 1000);
      if (seg < 60) return `hace ${seg}s`;
      return `hace ${Math.floor(seg / 60)}m`;
    }
  },
  watch: {
    // ⭐ Resetear paginación al cambiar de pestaña
    pestanaActiva() {
      this.paginaActual = 1;
    },
    totalPaginas(nuevo) {
      if (this.paginaActual > nuevo) {
        this.paginaActual = nuevo;
      }
    }
  },
  async mounted() {
    await this.load();
    this.iniciarPolling();
    this.tickInterval = setInterval(() => { this.$forceUpdate(); }, 5000);
  },
  beforeUnmount() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
    if (this.tickInterval) clearInterval(this.tickInterval);
  },
  methods: {
    // ⭐ Saber si está entregado o rechazado
    esEntregado(a) {
      return ['entregado', 'rechazado'].includes(a.estado);
    },

    barraEstado(estado) {
      const c = {
        asignado: 'bg-purple-500',
        aceptado: 'bg-blue-500',
        en_camino: 'bg-orange-500',
        en_proceso: 'bg-amber-500',
        entregado: 'bg-emerald-500',
        rechazado: 'bg-rose-500',
        cancelado: 'bg-gray-400',
      };
      return c[estado] || 'bg-gray-300';
    },

    labelMetodoPago(m) {
      if (!m) return 'N/A';
      const map = {
        'efectivo': 'EFECTIVO',
        'transferencia': 'TRANSF.',
        'pago_movil': 'P.MÓVIL',
        'pago movil': 'P.MÓVIL',
        'pagomovil': 'P.MÓVIL',
        'zelle': 'ZELLE',
        'usdt': 'USDT',
      };
      return map[String(m).toLowerCase().trim()] || String(m).toUpperCase();
    },

    getMetodoPagoColor(m) {
      const metodo = String(m || '').toLowerCase().trim();
      const c = {
        'efectivo': 'bg-emerald-200 text-emerald-800',
        'transferencia': 'bg-blue-200 text-blue-800',
        'pago_movil': 'bg-purple-200 text-purple-800',
        'pago movil': 'bg-purple-200 text-purple-800',
        'pagomovil': 'bg-purple-200 text-purple-800',
        'zelle': 'bg-indigo-200 text-indigo-800',
        'usdt': 'bg-teal-200 text-teal-800',
      };
      return c[metodo] || 'bg-gray-200 text-gray-800';
    },

    labelEstadoPago(a) {
      if (a.estadoPago === 'pagado' || a.estado === 'PAGO_VERIFICADO') return 'PAGADO';
      if (a.estadoPago === 'parcial') return 'PARCIAL';
      if (a.estadoPago === 'por_verificar' || a.estado === 'PAGO_PENDIENTE') return 'POR VERIF.';
      return 'SIN PAGO';
    },

    getEstadoPagoColor(estadoPago) {
      const c = {
        'pagado': 'bg-emerald-200 text-emerald-800',
        'parcial': 'bg-amber-200 text-amber-800',
        'por_verificar': 'bg-teal-200 text-teal-800',
        'verificado': 'bg-teal-200 text-teal-800',
        'pendiente': 'bg-rose-200 text-rose-800',
      };
      return c[estadoPago] || 'bg-rose-200 text-rose-800';
    },

    formatFechaCorta(f) {
      if (!f) return '-';
      try {
        const d = new Date(f);
        const diffMin = Math.floor((new Date() - d) / 60000);
        if (diffMin < 1) return 'ahora';
        if (diffMin < 60) return `${diffMin}m`;
        if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h`;
        return d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short' });
      } catch { return f; }
    },

    formatearMoneda(valor) {
      if (valor === null || valor === undefined || valor === '') return '0.00';
      const num = Number(valor);
      if (isNaN(num)) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);
    },

    formatearNumero(valor) {
      if (valor === null || valor === undefined || valor === '') return '0';
      const num = Number(valor);
      if (isNaN(num)) return '0';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      }).format(num);
    },

    getWhatsappLink(telefono, numeroPedido, nombreCliente = '') {
      if (!telefono) return '#';
      let limpio = String(telefono).replace(/\D/g, '');
      if (limpio.length === 10 && !limpio.startsWith('58')) limpio = '58' + limpio;
      else if (limpio.length === 11 && limpio.startsWith('0')) limpio = '58' + limpio.substring(1);
      const saludo = nombreCliente ? `Hola ${nombreCliente}, ` : 'Hola, ';
      const mensaje = `${saludo}soy tu delivery de EXTRA GAS. Estoy en camino con tu pedido #${numeroPedido}.`;
      return `https://wa.me/${limpio}?text=${encodeURIComponent(mensaje)}`;
    },

    async load() {
      this.loading = true;
      try {
        const res = await obtenerMisAsignaciones();
        if (res?.success === true) {
          const lista = Array.isArray(res.data) ? res.data : [];
          const idsAnt = new Set(this.asignaciones.map(a => a.idAsignacion));
          const nuevas = lista.filter(a => !idsAnt.has(a.idAsignacion));
          if (nuevas.length > 0 && this.asignaciones.length > 0) {
            nuevas.forEach(a => this.nuevasIds.add(a.idAsignacion));
            this.reproducirSonido();
            this.toast.info(`📦 ${nuevas.length} nueva(s)`);
          }
          this.asignaciones = lista;
          this.ultimaActualizacion = Date.now();
        }
      } catch (error) {
        this.toast.error(error?.message || 'Error');
      } finally {
        this.loading = false;
      }
    },

    iniciarPolling() {
      this.pollingInterval = setInterval(async () => {
        if (this.saving || this.showRechazarModal || this.showCompletarModal) return;
        await this.refreshSilencioso();
      }, 15000);
    },

    async refreshSilencioso() {
      try {
        const res = await obtenerMisAsignaciones();
        if (!res.success) return;
        const lista = Array.isArray(res.data) ? res.data : [];
        const idsAnt = new Set(this.asignaciones.map(a => a.idAsignacion));
        const nuevas = lista.filter(a => !idsAnt.has(a.idAsignacion));
        if (nuevas.length > 0) {
          nuevas.forEach(a => this.nuevasIds.add(a.idAsignacion));
          this.reproducirSonido();
          this.toast.info(`📦 Nueva: #${nuevas[0].numeroPedido}`);
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('🛵 Nueva asignación', { body: `#${nuevas[0].numeroPedido}` });
          }
        }
        this.asignaciones = lista;
        this.ultimaActualizacion = Date.now();
      } catch (e) {}
    },

    reproducirSonido() {
      if (!this.sonidoActivo) return;
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
      } catch (e) {}
    },

    async aceptar(a) {
      this.nuevasIds.delete(a.idAsignacion);
      this.saving = true;
      try {
        const res = await aceptarAsignacion(a.idAsignacion);
        if (res.success === true) {
          this.toast.success(`✅ #${a.numeroPedido}`);
          await this.load();
        } else this.toast.error(res.error || 'Error');
      } catch (error) {
        this.toast.error(error?.message || 'Error');
      } finally { this.saving = false; }
    },

    abrirModalRechazar(a) {
      this.asignacionARechazar = a;
      this.motivoRechazo = '';
      this.showRechazarModal = true;
    },

    async ejecutarRechazo() {
      if (!this.motivoRechazo.trim()) {
        this.toast.warning('El motivo es obligatorio');
        return;
      }
      if (!this.asignacionARechazar) return;
      this.saving = true;
      try {
        const res = await rechazarAsignacion(this.asignacionARechazar.idAsignacion, this.motivoRechazo.trim());
        if (res.success === true) {
          this.nuevasIds.delete(this.asignacionARechazar.idAsignacion);
          this.toast.success(`#${this.asignacionARechazar.numeroPedido} rechazado`);
          this.showRechazarModal = false;
          this.asignacionARechazar = null;
          this.motivoRechazo = '';
          await this.load();
        } else this.toast.error(res.error || 'Error');
      } catch (error) {
        this.toast.error(error?.message || 'Error');
      } finally { this.saving = false; }
    },

    async iniciarRuta(a) {
      this.nuevasIds.delete(a.idAsignacion);
      this.saving = true;
      try {
        const res = await iniciarRutaAsignacion(a.idAsignacion);
        if (res.success === true) {
          this.toast.success(`🚀 En camino`);
          await this.load();
        } else this.toast.error(res.error || 'Error');
      } catch (error) {
        this.toast.error(error?.message || 'Error');
      } finally { this.saving = false; }
    },

    async marcarLlegada(a) {
      this.nuevasIds.delete(a.idAsignacion);
      this.saving = true;
      try {
        const res = await marcarLlegadaAsignacion(a.idAsignacion);
        if (res.success === true) {
          this.toast.success(`📍 Llegaste`);
          await this.load();
        } else this.toast.error(res.error || 'Error');
      } catch (error) {
        this.toast.error(error?.message || 'Error');
      } finally { this.saving = false; }
    },

    abrirModalCompletar(a) {
      this.asignacionACompletar = a;
      this.codigoEntrega = '';
      this.observacionesEntrega = '';
      this.propinaBs = null;
      this.errorCodigo = '';
      this.showCompletarModal = true;

      nextTick(() => {
        this.$refs.inputCodigo?.focus();
      });
    },

    cerrarModalCompletar() {
      if (this.saving) return;
      this.showCompletarModal = false;
      this.asignacionACompletar = null;
      this.codigoEntrega = '';
      this.observacionesEntrega = '';
      this.propinaBs = null;
      this.errorCodigo = '';
    },

    // ⭐ Permite PEGAR el código
    handlePaste(e) {
      e.preventDefault();
      const texto = (e.clipboardData || window.clipboardData).getData('text');
      const limpio = String(texto || '').replace(/\D/g, '').substring(0, 6);
      this.codigoEntrega = limpio;
      this.errorCodigo = '';

      if (limpio.length === 6) {
        setTimeout(() => {
          if (this.codigoEntrega.length === 6 && !this.saving) {
            this.ejecutarCompletar();
          }
        }, 400);
      }
    },

    formatearCodigoInput(e) {
      let valor = String(e.target.value || '').replace(/\D/g, '');
      if (valor.length > 6) valor = valor.substring(0, 6);

      if (this.codigoEntrega !== valor) {
        this.codigoEntrega = valor;
      }
      this.errorCodigo = '';

      if (valor.length === 6) {
        setTimeout(() => {
          if (this.codigoEntrega.length === 6 && !this.saving) {
            this.ejecutarCompletar();
          }
        }, 500);
      }
    },

    async ejecutarCompletar() {
      if (!this.asignacionACompletar) return;
      if (this.saving) return;

      if (!this.codigoEntrega || this.codigoEntrega.length !== 6) {
        this.errorCodigo = 'El código debe tener 6 dígitos';
        this.$refs.inputCodigo?.focus();
        return;
      }

      // Guardar referencias ANTES de limpiar
      const idAsignacion = this.asignacionACompletar.idAsignacion;
      const numeroPedido = this.asignacionACompletar.numeroPedido;
      const idDelivery = this.asignacionACompletar.idDelivery
        || this.asignacionACompletar.deliveryId
        || null;

      this.saving = true;
      this.errorCodigo = '';

      try {
        const payload = {
          codigoEntrega: this.codigoEntrega,
          observaciones: this.observacionesEntrega?.trim() || null,
          propinaBs: this.propinaBs ? Number(this.propinaBs) : null,
        };

        console.log('📤 Completando entrega:', payload);

        const res = await completarEntregaAsignacion(idAsignacion, payload);

        if (res.success === true) {
          this.nuevasIds.delete(idAsignacion);
          this.toast.success(`✅ Pedido #${numeroPedido} entregado`);

          // ⭐ Cerrar modal explícitamente
          this.showCompletarModal = false;
          this.asignacionACompletar = null;
          this.codigoEntrega = '';
          this.observacionesEntrega = '';
          this.propinaBs = null;
          this.errorCodigo = '';

          // ⭐ Refuerzo: marcar delivery como disponible
          if (idDelivery) {
            try {
              await actualizarDisponibilidadDelivery(idDelivery, true);
              console.log('🛵 Delivery marcado disponible:', idDelivery);
            } catch (e) {
              console.warn('No se pudo marcar disponible:', e);
            }
          }

          // ⭐ Recargar lista
          await this.load();

          // ⭐ Cambiar automáticamente a pestaña "Entregados" para que vea el resultado
          // (opcional, quítalo si no lo quieres)
          // this.pestanaActiva = 'entregados';
        } else {
          this.errorCodigo = res.error || 'Código incorrecto';
          this.toast.error(res.error || 'Error al completar');
          this.codigoEntrega = '';

          nextTick(() => {
            this.$refs.inputCodigo?.focus();
          });
        }
      } catch (error) {
        console.error('💥 Error completando:', error);
        const msg = error?.message || 'Error al completar entrega';
        this.errorCodigo = msg.includes('código') ? msg : '';
        this.toast.error(msg);
      } finally {
        this.saving = false;
      }
    },
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }

@media (max-width: 480px) {
  .text-\[10px\] { font-size: 10px; }
  .text-\[9px\] { font-size: 9px; }
  .text-\[8px\] { font-size: 8px; }
}

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
}
.animate-shake { animation: shake 0.4s ease-in-out; }

.card-list-enter-active,
.card-list-leave-active {
  transition: all 0.4s ease;
}
.card-list-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
.card-list-leave-to {
  opacity: 0;
  transform: translateX(30px) scale(0.95);
}
.card-list-move {
  transition: transform 0.4s ease;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from > div,
.modal-leave-to > div {
  transform: translateY(30px) scale(0.95);
  opacity: 0;
}

* {
  -webkit-tap-highlight-color: transparent;
}
</style>